import React, { useMemo } from "react";
import { useDispatch } from "react-redux";
import Checkbox from "../../../common/Checkbox";
import IconToolTip from "../../../common/IconToolTip";
import OverflowTip from "../../../common/OverflowTip";
import { validateDateValue } from "../../../utils/common";
import StatusLabel from "../../Payments/reusableComponents/StatusLabel";
import { copyToClipboard } from "../../../actions/utils";
import { TERMINAL_COLOR } from "../TicketTable";
import { useSelector } from "react-redux";
import { openCustomModal } from "../../../actions/modal";
import { eligibleForServiceRequestExecutionDate, getNextExecutionDate, TicketMilestoneEvents } from "../TicketController";
import moment from "moment";
import { openChatModal } from "../../../actions/chat";
import ExecutionDate from "../Modal/ExecutionDate";
import { MilestoneSubStatusColor } from "./Status";
import { showIntalkWidget } from "../../../actions/intalk";
import { sendToastMessage } from "../../../actions/toast";
import { canPlaceOutboundCalls } from "../../../utils/access";
import TravelLogModal from "../Modal/TravelLogModal";
import { shouldShowEmptyTravelLogAction } from "./travelLogVisibility";


const RenderActions = ({ ticket }) => {
    const dispatch = useDispatch();
    const formButtonLoading = useSelector(state => state.loading.formButtonLoading);
    const { isCallInProgress } = useSelector(state => state.intalk);
    const newLoggedUserData = useSelector((state) => state.auth.newLoggedUserData);
    const editExecutionDateClicked = () => {
        const heading = (
            <span className='text-black'>
                Edit Execution Date :&nbsp;<span className='text-scogobgsky text-font16'>{ticket.ticket_number}</span>
            </span>
        );
        dispatch(
            openCustomModal({
                heading,
                modalWidth: '60rem',
                modalHeight: 'auto',
                modalComponent: <ExecutionDate ticketId={ticket.id} milestones={ticket.milestones} agents={ticket.agents} />,
            })
        );
    }

    const onOpenChatCLick = ({ ticketId, buttonName, isRightClick = false }) => {
        dispatch(openChatModal({ chatId: ticketId, buttonName, isRightClick, formName: `open_chat_${ticketId}` }));
    };

    let remote_teminal_connection_status = '';
    if (!['', undefined, null].includes(ticket.remote_teminal_connection_status)) {
        remote_teminal_connection_status = ticket.remote_teminal_connection_status;
    }

    const copyTicketDetaisValue = (ticket) => {
        dispatch(copyToClipboard({ data: `${ticket.ticket_number}` }));
    };


    const canEdit = useMemo(() => {
        return eligibleForServiceRequestExecutionDate(ticket)
    }, [ticket]);


    const signOffLockedMilestones = useMemo(() => {
        return ticket.milestones.filter((milestone) => milestone.is_signoff_locked);
    }, [ticket]);

    // Extract phone number for click-to-call functionality
    const contactInfo = useMemo(() => {
        let phone_number = '';
        let first_name = '';
        let last_name = '';
        let email = '';

        // Try to get contact info from various sources in order of preference
        if (ticket?.owner?.mobile) {
            phone_number = ticket.owner.mobile;
            first_name = ticket.owner.name || '';
        } else if (ticket?.location?.owners?.length > 0) {
            const owner = ticket.location.owners[0];
            phone_number = owner.mobile || '';
            first_name = owner.name || '';
            email = owner.email || '';
        } else if (ticket.requester_phone_number) {
            phone_number = ticket.requester_phone_number;
            first_name = ticket.requester_name || '';
        }

        return {
            phone_number: phone_number?.toString().replace(/\D/g, ''),
            first_name,
            last_name,
            email,
            contact_unique_id: ticket.ticket_number || ticket.id
        };
    }, [ticket]);

    const handleClickToCall = async () => {
        // Check if already on a call
        if (isCallInProgress) {
            dispatch(sendToastMessage({
                status: 'warning',
                message: 'You are already on a call. Please end the current call before starting a new one.'
            }));
            return;
        }

        // Check microphone and audio permissions first
        try {
            const stream = await navigator.mediaDevices.getUserMedia({
                audio: true,
                video: false
            });

            // Stop the stream immediately as we just needed to check permissions
            stream.getTracks().forEach(track => track.stop());
        } catch (error) {
            console.error('Microphone/Audio permission denied:', error);

            // Check if the error is due to permission denial
            if (error.name === 'NotAllowedError' || error.name === 'PermissionDeniedError') {
                dispatch(sendToastMessage({
                    status: 'danger',
                    message: 'Microphone access is required for making calls. Please click "Allow" when prompted and try again.'
                }));
            } else {
                dispatch(sendToastMessage({
                    status: 'danger',
                    message: 'Unable to access microphone. Please check your device settings and try again.'
                }));
            }
            return;
        }

        // Show loading toast while initializing
        dispatch(sendToastMessage({
            status: 'warning',
            message: 'Initializing call service...'
        }));

        // Now show the widget - this will trigger the initialization in IntalkGlobalWidget
        dispatch(showIntalkWidget({
            phone_number: contactInfo.phone_number?.length > 10 ? contactInfo.phone_number.slice(-10) : contactInfo.phone_number,
            first_name: contactInfo.first_name,
            last_name: contactInfo.last_name,
            contact_unique_id: ticket.id,
            ticket_number: ticket.ticket_number,
            ticket_id: ticket.id,
            user_id: newLoggedUserData.user.id
        }));
    };

    return <>
        <FEStatusIcon ticket={ticket} />
        <IconToolTip title={'Copy Ticket Number'}>
            <span
                className='material-icons text-scogobgsky hover:text-scogoorange text-font14 cursor-pointer'
                onClick={() => {
                    copyTicketDetaisValue(ticket);
                }}
            >
                content_copy
            </span>
        </IconToolTip>
        <TerminalIcon status={remote_teminal_connection_status} consoleUrl={ticket?.consoleUrl} />
        {/* Click to Call Icon */}
        {canPlaceOutboundCalls(newLoggedUserData.actions) && (
            <IconToolTip title={`Call ${contactInfo.first_name || 'Customer'}: ${contactInfo.phone_number}`}>
                <span
                    className='material-icons text-scogobgsky hover:text-scogoorange text-font14 cursor-pointer'
                    onClick={handleClickToCall}
                >
                    phone
                </span>
            </IconToolTip>
        )}
        {formButtonLoading[`open_chat_${ticket.id}`] ? <span className='text-font13 material-icons text-scogobgsky animate-spin'>
            refresh
        </span> : (
            <IconToolTip
                title={'Chat'}
                onClick={() => onOpenChatCLick({ ticketId: ticket.id, buttonName: `open_chat_${ticket.id}` })}
            >
                <div onContextMenu={(event) => {
                    event.preventDefault();
                    onOpenChatCLick({ ticketId: ticket.id, buttonName: `open_chat_${ticket.id}`, ticket, isRightClick: true })
                }}><img src='/img/chat_colourful.png' alt='' className='w-5 cursor-pointer ' /> </div>
            </IconToolTip>
        )}

        {signOffLockedMilestones.length > 0 ? <IconToolTip title={`SP/FE can not raise signoff/signoff approval of ${signOffLockedMilestones.map((elem) => elem.type).join(', ')} milestones, as execution date is more than 7 days old`}>
            <span
                className={`material-icons text-scogoorange text-font15 cursor-pointer`}
            // onClick={() => {
            //     dispatch(openCustomModal({
            //         modalComponent: <UnlockSignoff ticketId={ticket.id} milestones={signOffLockedMilestones} />,
            //         heading: 'Unlock Signoff',
            //         modalHeight: 'auto',
            //         modalWidth: '50rem'
            //     }));
            // }}
            >
                lock
            </span>
        </IconToolTip> : <></>}
        {canEdit ? <IconToolTip title={`Edit Execution Date`}>
            <span>
                <span onClick={editExecutionDateClicked} className='pt-2 cursor-pointer material-icons text-scogogray hover:text-scogoorange text-font15'>edit_calendar</span>
            </span>
        </IconToolTip> : null}
    </>
};

const TicketNumber = ({ ticket, rowIndex, checkedTicketTrackerLocal, handleOnChangeCheckboxLocal, handleTicketNumberCLick = () => { }, showCheckBox }) => {

    const dispatch = useDispatch();
    const distanceTravelled = useMemo(() => {
        if (ticket.total_dist_travelled_km) {
            return parseFloat(ticket.total_dist_travelled_km).toFixed(3);
        }
        if (ticket.dist_travelled_meter) {
            return (ticket.dist_travelled_meter / 1000).toFixed(3);
        }
        return 0;
    }, [ticket?.dist_travelled_meter, ticket?.total_dist_travelled_km]);

    const handleDistanceTravelledClick = (ticket) => {
        dispatch(openCustomModal({
            modalComponent: <TravelLogModal ticketId={ticket.id} />,
            heading: `Travel Log for ${ticket.ticket_number}`,
            modalHeight: '50vh',
            modalWidth: '60rem'
        }));
    }

    const getSignOffRequestedDate = (milestone) => {
        let date = undefined;
        if (ticket.remarks) {
            date = ticket.remarks.find(remark => remark.action === TicketMilestoneEvents.MILESTONE_SIGN_OFF_REQUESTED && remark.milestone_id === milestone.id)?.created_at;
        }
        return date;
    }

    const isSignOffRequestHighlighted = (milestone) => {
        const signOffDate = getSignOffRequestedDate(milestone);
        if (!signOffDate) return false;

        const daysDiff = Math.floor((new Date() - new Date(signOffDate)) / (1000 * 60 * 60 * 24));
        return daysDiff >= 3 && daysDiff <= 7;
    }

    const toHighlightMilestones = ticket.milestones.filter(milestone => isSignOffRequestHighlighted(milestone));
    const highlightClasses = toHighlightMilestones.length > 0 ? 'text-shadow-lg text-scogoorange animate-pulse' : '';

    const getSignOffMessageText = () => {
        if (toHighlightMilestones.length === 0) return ticket.ticket_number;

        const oldestMilestone = toHighlightMilestones.reduce((oldest, current) => {
            const currentDate = getSignOffRequestedDate(current);
            const oldestDate = getSignOffRequestedDate(oldest);
            return new Date(currentDate) < new Date(oldestDate) ? current : oldest;
        });

        const signOffDate = getSignOffRequestedDate(oldestMilestone);
        const daysPending = Math.floor((new Date() - new Date(signOffDate)) / (1000 * 60 * 60 * 24));

        return `Please action signoff requested ${daysPending} days ago`;
    }

    const textToShow = getSignOffMessageText();

    return (
        <div className="flex flex-col justify-center w-full h-full">
            <div className='w-full pl-2 items-center flex'>
                {showCheckBox && <Checkbox
                    checkColor='text-scogoorange mr-2'
                    checked={checkedTicketTrackerLocal.has(rowIndex)}
                    onChange={() => {
                        handleOnChangeCheckboxLocal(rowIndex);
                    }}
                    dynamicSize={'1.2rem'}
                />}
                <div className='max-w-full'>
                    <span className={`relative max-w-[15rem] mx-auto text-font12   hover:text-scogoorange cursor-pointer text-scogobgsky font-medium ${highlightClasses}`}>
                        {highlightClasses ? <IconToolTip onClick={() => handleTicketNumberCLick(ticket)} title={textToShow}>
                            {ticket.ticket_number}
                        </IconToolTip> : <OverflowTip
                            onClick={() => {
                                handleTicketNumberCLick(ticket);
                            }}
                            textClass={'max-w-[15rem] mx-auto text-font12   hover:text-scogoorange cursor-pointer text-scogobgsky  font-medium'}
                            someLongText={ticket.ticket_number}
                        />}
                    </span>
                    <div className='flex items-center gap-x-2 p-0 m-0'>
                        <RenderActions ticket={ticket} />
                    </div >
                    <RenderFormmatedExecutionDate ticket={ticket} />
                    {
                        ticket.external_ticket_id && (
                            <div className='w-full truncate'>
                                <StatusLabel titleText='External Ticket Id' content={ticket.external_ticket_id} color={'scogo999'} labelClass='text-gray-900' />
                            </div>
                        )
                    }
                    {
                        distanceTravelled > 0 && (
                            <div onClick={() => {
                                handleDistanceTravelledClick(ticket);
                            }} className='w-[95%] truncate flex items-center cursor-pointer'>
                                <StatusLabel
                                    hoverText={`${distanceTravelled} kilometers`}
                                    titleText='Distance Travelled'
                                    content={
                                        <div className="flex items-center gap-1">
                                            <span className="material-icons text-font11 text-scogoprimary">directions_car</span>
                                            <span>{distanceTravelled} km</span>
                                        </div>
                                    }
                                    color={'scogoprimary'}
                                    labelClass='text-gray-700 font-medium'
                                />
                            </div>
                        )
                    }
                    <div className="pl-1 ticket_status pt-2 flex items-center gap-2">
                        <IconToolTip title={`Ticket Status: ${ticket.status?.replace(/_/g, ' ')}`}>
                            <span className={`max-w-9rem truncate p-1 items-center  capitalize ${MilestoneSubStatusColor[ticket?.status]} font-medium py-1`}>{`${ticket?.status?.replace(/_/g, ' ')}`}</span>
                        </IconToolTip>
                        {
                            shouldShowEmptyTravelLogAction(distanceTravelled, ticket.status) && (
                                <IconToolTip title='View Travel Log'>
                                    <span
                                        className='material-icons text-scogobgsky hover:text-scogoorange text-font15 cursor-pointer'
                                        onClick={() => handleDistanceTravelledClick(ticket)}
                                    >
                                        route
                                    </span>
                                </IconToolTip>
                            )
                        }
                    </div>
                </div >
            </div >
        </div >
    );

}

export default TicketNumber;


export const TerminalIcon = ({ status, consoleUrl }) => {

    const onTerminalClick = (consoleUrl) => {
        window.open(consoleUrl, '_blank');
    };

    if (!status) return <></>;

    return <IconToolTip title={'Remote Console'}>
        <span
            className={`material-icons ${TERMINAL_COLOR[status] || ''} bg-black text-font15 cursor-pointer`}
            onClick={() => {
                onTerminalClick(consoleUrl);
            }}
        >
            terminal
        </span>
    </IconToolTip>
}

const FEStatusIcon = ({ ticket }) => {
    let title = '';
    let class_name = '';
    let onClick = () => { };

    if (ticket.is_live_tracking_enabled === 1 && ticket?.reachedSite === 0) {
        title = 'FE On The Way';
        class_name = 'text-scogosuccess cursor-pointer';
        onClick = () => {
            if (ticket?.live_tracking_url) {
                window.open(ticket?.live_tracking_url);
            }
        }
    } else if (ticket.is_live_tracking_enabled === 2 && ticket?.reachedSite === 0) {
        title = 'Tracking Request Sent';
        class_name = 'text-yellow-500';
        onClick = () => { };
    } else if (ticket.is_live_tracking_enabled === 1 && ticket?.reachedSite === 1) {
        title = 'FE has reached site with enabled tracking';
        class_name = 'text-scogoprimary';
        onClick = () => { };
    }

    if (title) {
        return (
            <IconToolTip title={title}>
                <span
                    onClick={onClick}
                    className={`material-icons ${class_name} text-font14`}
                >
                    directions_bike
                </span>
            </IconToolTip>
        );
    }

    return <></>;
}

const RenderFormmatedExecutionDate = ({ ticket }) => {

    const { date, label } = getNextExecutionDate(ticket);
    if (validateDateValue(date)) {
        let elem = <></>;
        elem = <p className="flex">
            <b>{moment(date).format('DD-MM-YYYY')}</b>
            <b className="pl-1">{moment(date).format('hh:mm A')}</b>
        </p>
        return (
            <IconToolTip title={label}>
                <span>
                    <span className={'text-font09 text-scogogray'}>{elem}</span>
                </span>
            </IconToolTip>
        );
    }
};
